import { useCallback, useEffect, useState } from 'react';
import { FlatList, Image, RefreshControl, Text, View } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../context/AuthContext';
import { attachTopupProof, getBalance, getTopupProofToken, getTransactions, requestTopup, withdraw } from '../api/wallet';
import { WalletTransaction } from '../types';
import { CameraCapture } from '../components/CameraCapture';
import {
  Button,
  Caption,
  Card,
  CardSkeleton,
  CoinIcon,
  ConfirmDialog,
  EmptyState,
  ErrorText,
  FormInput,
  GradientCard,
  IconBadge,
  Label,
  ScreenContainer,
  Title,
} from '../components/ui';
import { colors, fonts, gradients } from '../theme';
import { ApiError } from '../api/client';
import { myTopupProofUrl } from '../config';
import { REVIEWER_TIER_LABELS } from '../constants/labels';

/** Fetches its own short-lived photo token — a session-wide token can't be
 * embedded directly in the image URL (see backend JwtPhotoStrategy). */
function TopupProofThumbnail({ token, transactionId }: { token: string; transactionId: string }) {
  const [photoToken, setPhotoToken] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getTopupProofToken(token, transactionId)
      .then((r) => {
        if (!cancelled) setPhotoToken(r.token);
      })
      .catch(() => {
        if (!cancelled) setPhotoToken(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, transactionId]);
  if (!photoToken) return null;
  return (
    <Image
      source={{ uri: myTopupProofUrl(transactionId, photoToken) }}
      style={{ width: 120, height: 80, borderRadius: 8, marginTop: 8 }}
      resizeMode="cover"
    />
  );
}

const TX_LABELS: Record<string, string> = {
  TOPUP: 'Nạp tiền',
  TOPUP_BONUS: 'Thưởng ngày nạp',
  ESCROW_HOLD: 'Giữ xu cho nhiệm vụ',
  ESCROW_REFUND: 'Hoàn xu (hủy nhiệm vụ)',
  REWARD_PAYOUT: 'Nhận thưởng',
  WITHDRAWAL: 'Rút xu',
  ADMIN_ADJUST: 'Điều chỉnh',
  PLATFORM_FEE: 'Phí dịch vụ nền tảng',
  REFERRAL_BONUS: 'Thưởng giới thiệu',
};

const TX_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  TOPUP: 'add-circle-outline',
  TOPUP_BONUS: 'gift-outline',
  ESCROW_HOLD: 'lock-closed-outline',
  ESCROW_REFUND: 'return-up-back-outline',
  REWARD_PAYOUT: 'trophy-outline',
  WITHDRAWAL: 'arrow-down-circle-outline',
  ADMIN_ADJUST: 'construct-outline',
  PLATFORM_FEE: 'receipt-outline',
  REFERRAL_BONUS: 'people-outline',
};

const TX_PAGE_SIZE = 50;

const TX_STATUS_LABELS: Record<string, string> = {
  COMPLETED: 'Hoàn tất',
  PENDING: 'Đang chờ xử lý',
  FAILED: 'Thất bại',
};

export default function WalletScreen() {
  const { token, user, activeMode, refreshUser } = useAuth();
  const isOwner = activeMode === 'SHOP_OWNER';
  const [balance, setBalance] = useState(user?.coinBalance ?? 0);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [amount, setAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [withdrawPassword, setWithdrawPassword] = useState('');
  const [proofTargetId, setProofTargetId] = useState<string | null>(null);
  const [proofCameraOpen, setProofCameraOpen] = useState(false);
  const [proofUploading, setProofUploading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [b, tx] = await Promise.all([getBalance(token), getTransactions(token, 1, TX_PAGE_SIZE)]);
      setBalance(b.coinBalance);
      setTransactions(tx.transactions);
      setTxTotal(tx.total);
    } catch (e) {
      // Previously swallowed silently — balance/history just stayed stale
      // with no indication the fetch actually failed.
      setError(e instanceof ApiError ? e.message : 'Không tải được số dư/lịch sử giao dịch, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadMoreTransactions = async () => {
    if (!token || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = Math.floor(transactions.length / TX_PAGE_SIZE) + 1;
      const tx = await getTransactions(token, nextPage, TX_PAGE_SIZE);
      setTransactions((prev) => [...prev, ...tx.transactions]);
      setTxTotal(tx.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Không tải thêm được, vui lòng thử lại.');
    } finally {
      setLoadingMore(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onSubmit = () => {
    setError(null);
    setMessage(null);
    const value = Number(amount);
    if (!value || value <= 0) {
      setError('Nhập số tiền hợp lệ');
      return;
    }
    if (isOwner) {
      performAction(value);
    } else {
      // Withdrawing moves real money out irreversibly once an admin
      // processes it — always confirm before submitting the request.
      setConfirmVisible(true);
    }
  };

  const performAction = async (value: number) => {
    if (!token) return;
    // Withdrawing moves real money out — require the account password again
    // here, even though the session token is already valid, so a stolen/
    // borrowed unlocked phone can't be used to drain the balance.
    if (!isOwner && !withdrawPassword) {
      setError('Nhập mật khẩu để xác nhận rút xu');
      return;
    }
    setActionLoading(true);
    try {
      if (isOwner) {
        const result = await requestTopup(token, value);
        setMessage(
          result.bankInstructions
            ? `Đã gửi yêu cầu nạp ${value.toLocaleString('vi-VN')}đ. Vui lòng chuyển khoản theo: ${result.bankInstructions}. Sau khi chuyển khoản, gửi ảnh chụp màn hình bên dưới để admin xác nhận nhanh hơn.`
            : `Đã gửi yêu cầu nạp ${value.toLocaleString('vi-VN')}đ. Vui lòng liên hệ admin để được hướng dẫn chuyển khoản — xu sẽ được cộng sau khi admin xác nhận đã nhận tiền.`,
        );
        // Prompt the just-created request's proof step right away — most
        // users will have the transfer confirmation on screen at this moment.
        setProofTargetId(result.transaction.id);
      } else {
        const result = await withdraw(token, value, withdrawPassword);
        setMessage(
          `Đã gửi yêu cầu rút ${result.grossCoins} xu (phí ${result.withdrawalFeeBp / 100}%: ${result.feeCoins} xu) — thực nhận quy đổi ${result.netCoins} xu, chờ xử lý`,
        );
      }
      setAmount('');
      setWithdrawPassword('');
      await Promise.all([load(), refreshUser()]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Thao tác thất bại');
    } finally {
      setActionLoading(false);
      setConfirmVisible(false);
    }
  };

  const onProofCaptured = async (uri: string) => {
    if (!token || !proofTargetId) return;
    setProofCameraOpen(false);
    setProofUploading(true);
    setError(null);
    try {
      await attachTopupProof(token, proofTargetId, uri);
      setMessage('Đã gửi ảnh chuyển khoản — admin sẽ đối chiếu và xác nhận sớm.');
      setProofTargetId(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Gửi ảnh thất bại, vui lòng thử lại');
    } finally {
      setProofUploading(false);
    }
  };

  if (proofCameraOpen) {
    return <CameraCapture onCapture={onProofCaptured} onCancel={() => setProofCameraOpen(false)} />;
  }

  return (
    <ScreenContainer safeTop safeBottom>
      <GradientCard colors={gradients.wallet} style={{ paddingVertical: 26 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Ionicons name="wallet-outline" size={15} color="rgba(255,255,255,0.8)" />
          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: fonts.medium }}>
            Số dư hiện tại
          </Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <Text style={{ fontSize: 38, fontFamily: fonts.extraBold, color: '#fff' }}>
            {balance.toLocaleString('vi-VN')}
          </Text>
          <CoinIcon size={30} />
        </View>
      </GradientCard>

      <Card>
        <Label>{isOwner ? 'Nạp tiền vào ví Loot' : 'Rút xu'}</Label>
        {isOwner ? (
          <Caption style={{ marginBottom: 8 }}>
            Nhập số tiền bạn sẽ chuyển khoản. Xu chỉ được cộng vào ví sau khi admin xác nhận đã nhận được tiền.
          </Caption>
        ) : null}
        {!isOwner && user?.withdrawalFeePercent != null ? (
          <Caption style={{ marginBottom: 8 }}>
            Rút tối thiểu 100 xu. Phí rút hiện tại: {user.withdrawalFeePercent}% (hạng {REVIEWER_TIER_LABELS[user.reviewerTier ?? ''] ?? user.reviewerTier})
            {!user.hasIdentityOnFile ? ' — cần khai báo thông tin nhận tiền ở mục Hồ sơ trước khi rút' : ''}
          </Caption>
        ) : null}
        <FormInput
          value={amount}
          onChangeText={setAmount}
          keyboardType="numeric"
          placeholder={isOwner ? 'VD: 500000' : 'Số xu muốn rút'}
        />
        <ErrorText>{error}</ErrorText>
        {message ? <Text style={{ color: colors.success, marginBottom: 10 }}>{message}</Text> : null}
        <Button
          title={isOwner ? 'Nạp tiền' : 'Yêu cầu rút xu'}
          onPress={onSubmit}
          loading={actionLoading}
        />
        {isOwner && proofTargetId ? (
          <View style={{ marginTop: 4 }}>
            <Button
              title="Gửi ảnh chuyển khoản"
              variant="outline"
              loading={proofUploading}
              onPress={() => setProofCameraOpen(true)}
            />
          </View>
        ) : null}
      </Card>

      <ConfirmDialog
        visible={confirmVisible}
        title="Xác nhận rút xu"
        message={`Bạn có chắc muốn gửi yêu cầu rút ${amount ? Number(amount).toLocaleString('vi-VN') : ''} xu? Yêu cầu sẽ được admin xử lý và không thể tự huỷ sau khi gửi.`}
        confirmLabel="Xác nhận rút"
        loading={actionLoading}
        onConfirm={() => performAction(Number(amount))}
        onCancel={() => {
          setConfirmVisible(false);
          setWithdrawPassword('');
        }}
      >
        <View style={{ marginTop: 10 }}>
          <FormInput
            value={withdrawPassword}
            onChangeText={setWithdrawPassword}
            placeholder="Nhập mật khẩu để xác nhận"
            secureTextEntry
            autoFocus
          />
        </View>
      </ConfirmDialog>

      <Title style={{ marginTop: 8, marginBottom: 8 }}>Lịch sử giao dịch</Title>
      {loading && transactions.length === 0 ? (
        <>
          <CardSkeleton />
          <CardSkeleton />
        </>
      ) : (
        <FlatList
          data={transactions}
          keyExtractor={(item) => item.id}
          refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
          ListEmptyComponent={!loading && !error ? <EmptyState text="Chưa có giao dịch nào" icon="receipt-outline" /> : null}
          ListFooterComponent={
            transactions.length < txTotal ? (
              <Button title="Xem thêm" variant="outline" onPress={loadMoreTransactions} loading={loadingMore} />
            ) : null
          }
          renderItem={({ item }) => (
            <Card style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
              <IconBadge
                name={TX_ICONS[item.type] ?? 'swap-horizontal-outline'}
                tone={item.amountCoins >= 0 ? 'success' : 'muted'}
                size={17}
              />
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Text style={{ fontFamily: fonts.semiBold, color: colors.text }}>
                    {TX_LABELS[item.type] ?? item.type}
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <Text
                      style={{
                        fontFamily: fonts.bold,
                        color: item.amountCoins >= 0 ? colors.success : colors.danger,
                      }}
                    >
                      {item.amountCoins >= 0 ? '+' : ''}
                      {item.amountCoins}
                    </Text>
                    <CoinIcon size={14} />
                  </View>
                </View>
                {item.note ? (
                  <Text style={{ color: colors.textMuted, fontSize: 12, marginTop: 4 }}>
                    {item.note}
                  </Text>
                ) : null}
                <Text style={{ color: colors.textMuted, fontSize: 11, marginTop: 4 }}>
                  {new Date(item.createdAt).toLocaleString('vi-VN')} · {TX_STATUS_LABELS[item.status] ?? item.status}
                </Text>
                {isOwner && item.type === 'TOPUP' && item.status === 'PENDING' ? (
                  item.proofPhotoUrl ? (
                    token ? <TopupProofThumbnail token={token} transactionId={item.id} /> : null
                  ) : (
                    <View style={{ marginTop: 8, alignSelf: 'flex-start' }}>
                      <Button
                        title="Gửi ảnh chuyển khoản"
                        variant="outline"
                        onPress={() => {
                          setProofTargetId(item.id);
                          setProofCameraOpen(true);
                        }}
                      />
                    </View>
                  )
                ) : null}
              </View>
            </Card>
          )}
        />
      )}
    </ScreenContainer>
  );
}
